"use client";
import React from "react";

import { Box, BoxProps } from "@mui/material";
import { styled } from "@mui/material/styles";

import {
  COLOR_GREYSCALE_BLACK_54,
  COLOR_GREYSCALE_BLACK_8,
  COLOR_PRIMARY_BLACK,
  COLOR_PRIMARY_WHITE,
  COLOR_WARNING_RED,
} from "~/theme/colors";
import { getFormatMedia } from "~/theme/utils";

import { PRODUCT_CARD_CLASSES, REMOVED_PRODUCT_CARD_CLASSES } from "./constants";

interface StyleProps {
  isShoppingListLayout?: boolean;
}

export const ProductCardContainer = styled(
  ({ isShoppingListLayout, ...restProps }: StyleProps & BoxProps) => (
    <Box {...restProps} />
  ),
)(({ theme, isShoppingListLayout }) => {
  const formatMedia = getFormatMedia(theme);

  return {
    display: "block",
    paddingBottom: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    textDecoration: "none",
    borderBottom: isShoppingListLayout
      ? `1px solid ${COLOR_GREYSCALE_BLACK_8}`
      : "none",

    [formatMedia.BREAKPOINT_TABLET]: {
      borderBottom: isShoppingListLayout ? `unset` : "none",
      paddingTop: "unset",
    },
    "& > * a": {
      textDecoration: "none",
    },
    [`& .${PRODUCT_CARD_CLASSES.imageWrapper}`]: {
      display: "flex",
      position: "relative",
    },
    [`& .${PRODUCT_CARD_CLASSES.unpublishedProduct}`]: {
      "&:after": {
        content: "''",
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        background:
          "linear-gradient(0deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5))",
      },
    },
    [`& .${PRODUCT_CARD_CLASSES.image}`]: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      aspectRatio: "0.75 / 1",
    },
    [`& .${PRODUCT_CARD_CLASSES.tags}`]: {
      position: "absolute",
      bottom: 0,
      left: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
    },
    [`& .${PRODUCT_CARD_CLASSES.comingSoonTag}`]: {
      position: "absolute",
      top: 0,
      left: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
    },
    [`& .${PRODUCT_CARD_CLASSES.title}`]: {
      fontSize: "12px",
      fontFamily: "Primark Basis",
      marginTop: theme.spacing(2),
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      height: 40,
      "& > a": {
        textDecoration: "none",
      },
      "& > a:hover": {
        textDecoration: "underline",
      },
    },
    [`& .${PRODUCT_CARD_CLASSES.description}`]: {
      display: "flex",
      flexDirection: isShoppingListLayout ? "row" : "column",
      [formatMedia.BREAKPOINT_DESKTOP]: {
        flexDirection: "row",
        justifyContent: "space-between",
      },
    },
    [`& .${PRODUCT_CARD_CLASSES.price}`]: {
      fontWeight: 700,
      fontFamily: "Primark Basis",
      marginTop: theme.spacing(0.5),
    },
    [`& .${PRODUCT_CARD_CLASSES.priceLink}`]: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1, 1.25),
    },
    [`& .${PRODUCT_CARD_CLASSES.pricePerUnitText}`]: {
      color: COLOR_GREYSCALE_BLACK_54,
    },
    [`& .${PRODUCT_CARD_CLASSES.colors}`]: {
      fontSize: "12px",
      fontFamily: "Primark Basis",
      marginTop: theme.spacing(0.5),
      color: COLOR_GREYSCALE_BLACK_54,
    },
    [`& .${PRODUCT_CARD_CLASSES.shoppingListProductColor}`]: {
      marginTop: theme.spacing(2),
    },
    [`& .${PRODUCT_CARD_CLASSES.labelRow}`]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    [`& .${PRODUCT_CARD_CLASSES.oneSizeLabel}`]: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.375, 2),
      backgroundColor: COLOR_PRIMARY_WHITE,
      border: "1px solid rgba(0, 0, 0, 0.23)",
      color: COLOR_PRIMARY_BLACK,
    },
    [`& .${PRODUCT_CARD_CLASSES.sizeBox}`]: {
      marginTop: theme.spacing(1),
    },
    [`& .${PRODUCT_CARD_CLASSES.block}`]: {
      "&:first-of-type.title": {
        marginTop: 0,
      },
    },
    [`& .${PRODUCT_CARD_CLASSES.addToBag}`]: {
      marginTop: theme.spacing(1),
    },
    [`& .${PRODUCT_CARD_CLASSES.maxQuantityError}`]: {
      marginTop: theme.spacing(1),
    },
    [`& .${PRODUCT_CARD_CLASSES.sizeBox}`]: {
      marginTop: theme.spacing(1),
    },
    [`& .${PRODUCT_CARD_CLASSES.oneSizeLabel}`]: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.375, 2),
      backgroundColor: COLOR_PRIMARY_WHITE,
      border: "1px solid rgba(0, 0, 0, 0.23)",
      color: COLOR_PRIMARY_BLACK,
    },
    [`& .${PRODUCT_CARD_CLASSES.iconsGroup}`]: {
      display: "flex",
      justifyContent: "flex-end",

      [`& .MuiIconButton-root`]: {
        padding: theme.spacing(3.5),
        margin: theme.spacing(2, 1, 0, 1),
        [formatMedia.BREAKPOINT_DESKTOP]: {
          marginLeft: 0,
          marginTop: theme.spacing(2),
        },
      },
      [`&.${PRODUCT_CARD_CLASSES.shoppingIconsGroup} .MuiIconButton-root`]: {
        padding: 0,
      },
    },
    [`& .${PRODUCT_CARD_CLASSES.shoppingListProductStatus}`]: {
      display: "flex",
      alignItems: "center",
      marginTop: theme.spacing(2),
    },
    [`& .${PRODUCT_CARD_CLASSES.statusIcon}`]: {
      marginRight: theme.spacing(1),
    },
    [`& .${PRODUCT_CARD_CLASSES.addToBag}`]: {
      display: "flex",
      width: "100%",
      marginTop: theme.spacing(2),
      alignItems: "center",
    },
    [`& .${PRODUCT_CARD_CLASSES.maxQuantityError}`]: {
      color: COLOR_WARNING_RED,
      marginTop: theme.spacing(3),
      [formatMedia.BREAKPOINT_TABLET]: {
        marginTop: theme.spacing(2),
      },
    },
  };
});

export const RemovedProductCardContainer = styled(Box)(({ theme }) => {
  const formatMedia = getFormatMedia(theme);
  return {
    [`& .${REMOVED_PRODUCT_CARD_CLASSES.removedProduct}`]: {
      display: "flex",
      position: "relative",
      " &:after": {
        content: "''",
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        background:
          "linear-gradient(0deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5))",
      },
    },
    [`& .${REMOVED_PRODUCT_CARD_CLASSES.removedProductImage}`]: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      aspectRatio: "0.75 / 1",
      display: "none",
      [formatMedia.BREAKPOINT_TABLET]: {
        display: "block",
      },
    },
    [`& .${REMOVED_PRODUCT_CARD_CLASSES.removedProductDescription}`]: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(3),
      borderBottom: `1px solid ${COLOR_GREYSCALE_BLACK_8}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      [formatMedia.BREAKPOINT_TABLET]: {
        padding: theme.spacing(2, 0),
        border: "none",
      },
    },
    [`& .${REMOVED_PRODUCT_CARD_CLASSES.removedProductName}`]: {
      fontWeight: 700,
      [formatMedia.BREAKPOINT_TABLET]: {
        fontWeight: 400,
      },
    },
    [`& .${REMOVED_PRODUCT_CARD_CLASSES.undoButton}`]: {
      overflowWrap: "break-word",
      marginLeft: theme.spacing(2),
    },
  };
});
